/**
 * Anonymous Spot sales chat API (no Auth0 / Bearer required).
 */

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export type SpotChatRole = 'user' | 'assistant';

export type SpotChatMessage = {
  role: SpotChatRole;
  content: string;
};

export type SpotPageSnapshot = {
  text: string;
  links: Array<{ label: string; href: string }>;
  imageDescriptions: string[];
};

export type SpotChatStreamHandlers = {
  onDelta?: (delta: string) => void;
  onFinal?: (content: string) => void;
  onError?: (message: string) => void;
};

function parseSseBlock(raw: string): { eventName: string; data: string } | null {
  const lines = raw.split('\n');
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (!dataLines.length) return null;
  const data = dataLines.join('\n');
  if (data === '[DONE]') {
    return { eventName: 'done', data };
  }
  return { eventName, data };
}

export async function streamSpotChat(params: {
  message: string;
  history: SpotChatMessage[];
  pagePath: string;
  siteOrigin: string;
  pageSnapshot: SpotPageSnapshot;
  sessionId: string;
  signal?: AbortSignal;
  handlers: SpotChatStreamHandlers;
}): Promise<void> {
  if (!API_BASE) {
    params.handlers.onError?.('Chat is unavailable right now (API URL missing).');
    return;
  }

  const response = await fetch(`${API_BASE}/public/spot-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      message: params.message,
      history: params.history,
      pagePath: params.pagePath,
      siteOrigin: params.siteOrigin,
      pageSnapshot: params.pageSnapshot,
      sessionId: params.sessionId,
    }),
    signal: params.signal,
  });

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    params.handlers.onError?.(
      text || 'Spot is temporarily unavailable. Try again in a moment.',
    );
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let assembled = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    while (true) {
      const boundary = buffer.indexOf('\n\n');
      if (boundary === -1) break;
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseSseBlock(rawEvent);
      if (!parsed) continue;
      if (parsed.eventName === 'done') {
        params.handlers.onFinal?.(assembled.trim());
        return;
      }
      if (parsed.eventName === 'message_delta') {
        try {
          const payload = JSON.parse(parsed.data) as { delta?: string };
          if (payload.delta) {
            assembled += payload.delta;
            params.handlers.onDelta?.(payload.delta);
          }
        } catch {
          // ignore malformed delta
        }
      } else if (parsed.eventName === 'message') {
        try {
          const payload = JSON.parse(parsed.data) as { content?: string };
          if (typeof payload.content === 'string' && payload.content.trim()) {
            assembled = payload.content;
          }
        } catch {
          // ignore
        }
      } else if (parsed.eventName === 'error') {
        try {
          const payload = JSON.parse(parsed.data) as { message?: string };
          params.handlers.onError?.(
            payload.message || 'Spot could not answer just now.',
          );
        } catch {
          params.handlers.onError?.('Spot could not answer just now.');
        }
        return;
      }
    }
  }

  params.handlers.onFinal?.(assembled.trim());
}

export function getSpotBubbleDelayMs(): number {
  const fromEnv = Number(process.env.REACT_APP_SPOT_CHAT_BUBBLE_DELAY_MS);
  if (Number.isFinite(fromEnv) && fromEnv >= 0) {
    return fromEnv;
  }
  // Default: wait 20s so Spot does not interrupt the first glance.
  return 20_000;
}
